/** Runtime screenplay for the enhanced original story "BlueBob and the Missing Treasure Patties".
 * Every beat is a concrete shot with visible blocking, a physical prop, sound, and (when spoken)
 * a voiced line. The source treatment and screenplay are emitted by scripts/generate-enhanced-cutscenes.py.
 */

export const CHARACTERS = ['bluebob', 'krabs', 'king', 'zap'] as const
export type Character = (typeof CHARACTERS)[number]

export const SCENES = ['town', 'krabbyshack', 'jellytrail', 'coralcave', 'seaweed', 'bubblebridge', 'ship', 'deepsea', 'pattyreef', 'storm', 'royalcave', 'throne', 'reopening'] as const
export type SceneArt = (typeof SCENES)[number]

export const CUTSCENE_CAMERAS = ['wide', 'two-shot', 'close', 'reveal', 'chase', 'low', 'insert', 'reaction', 'tracking', 'overhead'] as const
export type CutsceneCamera = (typeof CUTSCENE_CAMERAS)[number]

export const CUTSCENE_ACTIONS = ["accuse", "anchor", "bounce", "brace", "break-spell", "build", "burst-open", "calm", "catch", "celebrate", "challenge", "chase", "collapse-throne", "collect", "command-pulse", "count", "dance", "decorate", "discover", "dive", "drop", "eat", "enter", "fall", "fight-over", "form-bubble", "glitch", "group-laugh", "hesitate", "hide", "hug-prop", "hum", "knock-over", "lower", "offer", "offer-coin", "open", "pin-sign", "point", "pop-bubble", "project-map", "protect", "pull", "repair", "reveal", "reward", "roll", "rotate-sign", "serve", "sever-command", "soften-glow", "spill", "spit-out", "stabilize", "startle", "steal", "summon-lightning", "summon-wave", "surround", "swallow", "swarm-carry", "talk", "threaten", "throw", "tilt", "trap", "walk", "watch", "wave"] as const
export type CutsceneAction = (typeof CUTSCENE_ACTIONS)[number]

export const CUTSCENE_ANIMATION_CLIPS = ["Accuse", "Anchor", "Bounce", "Brace", "BreakSpell", "Build", "BurstOpen", "Calm", "Catch", "Celebrate", "Challenge", "Chase", "CollapseThrone", "Collect", "CommandPulse", "Count", "Dance", "Decorate", "Discover", "Dive", "Drop", "Eat", "Fall", "FightOver", "FormBubble", "GroupLaugh", "Hesitate", "Hum", "KnockOver", "Lower", "Offer", "OfferCoin", "Open", "Point", "PopBubble", "ProjectMap", "Protect", "Pull", "Repair", "Reveal", "Reward", "Roll", "Serve", "SeverCommand", "Spill", "SpitOut", "Startle", "Steal", "SummonLightning", "SummonWave", "Surround", "SwarmCarry", "Talk", "Threaten", "Throw", "Tilt", "Trap", "Walk", "Watch", "Wave"] as const
export type AnimationClipName = (typeof CUTSCENE_ANIMATION_CLIPS)[number]

export type DioramaProp = 'coin-chest' | 'coins' | 'coin-bag' | 'jelly-drop' | 'sandwich' | 'sticky-sandwich' | 'sour-sandwich' | 'deepsea-sandwich' | 'giant-sandwich' | 'ultimate-sandwich' | 'jelly-jars' | 'bridge' | 'ship' | 'clam' | 'map' | 'patty' | 'throne' | 'sign' | 'web' | 'lightning' | 'decorations'
export interface ActionCue { type: CutsceneAction; actor: Character; clip?: AnimationClipName; target?: Character; prop?: DioramaProp; delay?: number }
export interface Dialogue { speaker: Character; text: string; emotion?: 'bright' | 'worried' | 'angry' | 'determined' | 'sheepish' | 'triumphant' | 'afraid' | 'gentle' | 'dry'; performance?: string }
export interface BeatAudio { music?: string; sfx?: string[]; silence?: boolean }
export interface Beat { shot: string; scene: SceneArt; camera: CutsceneCamera; cast: Character[]; actions: ActionCue[]; direction: string; dialogue?: Dialogue; audio?: BeatAudio; titleCards?: string[]; transition?: 'cut' | 'dissolve' | 'match-cut' | 'whip-pan' | 'gameplay-handoff'; duration?: number }
export interface Cutscene { id: string; title: string; beats: Beat[] }

export const CUTSCENE_THROW_RELEASE = 0.42
export const CUTSCENE_FOOD_CONTACT: Record<string, number> = {
  't2-1b-02': 0.7,
  't10-3-04': 0.75,
}

export const CUTSCENES: Record<string, Cutscene> = {
  "intro": {
    "id": "intro",
    "title": "A Bad Day at the Krabby Shack",
    "beats": [
      {
        "shot": "intro",
        "scene": "town",
        "camera": "wide",
        "cast": [
          "bluebob"
        ],
        "actions": [
          {
            "type": "enter",
            "actor": "bluebob",
            "clip": "Walk"
          },
          {
            "type": "walk",
            "actor": "bluebob",
            "clip": "Walk"
          },
          {
            "type": "hum",
            "actor": "bluebob",
            "clip": "Hum"
          }
        ],
        "direction": "The camera fades in on Bubble Bay as BlueBob walks down the sandy road, humming toward the Krabby Shack.",
        "transition": "cut",
        "audio": {
          "music": "Bubble Bay morning",
          "sfx": [
            "soft bubbles"
          ]
        },
        "duration": 2.8
      },
      {
        "shot": "intro-2a-01",
        "scene": "krabbyshack",
        "camera": "wide",
        "cast": [
          "bluebob",
          "krabs"
        ],
        "actions": [
          {
            "type": "count",
            "actor": "krabs",
            "clip": "Count",
            "prop": "coin-chest"
          },
          {
            "type": "hum",
            "actor": "bluebob",
            "clip": "Hum"
          }
        ],
        "direction": "Money Krabs counts a giant pile of coins on the Shack counter while BlueBob arrives at the door.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "coin clink"
          ]
        },
        "dialogue": {
          "speaker": "krabs",
          "text": "Ahhh, me beautiful money! Nothing could possibly go wrong today!",
          "emotion": "triumphant",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "intro-2b",
        "scene": "krabbyshack",
        "camera": "two-shot",
        "cast": [
          "bluebob",
          "krabs"
        ],
        "actions": [
          {
            "type": "wave",
            "actor": "bluebob",
            "clip": "Wave"
          },
          {
            "type": "startle",
            "actor": "krabs",
            "clip": "Startle"
          }
        ],
        "direction": "BlueBob waves from the doorway; the greeting startles Krabs into bumping the chest with his claw.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "comic pop"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "Hi, Money Krabs!",
          "emotion": "bright",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "intro-2c",
        "scene": "krabbyshack",
        "camera": "reaction",
        "cast": [
          "bluebob",
          "krabs"
        ],
        "actions": [
          {
            "type": "knock-over",
            "actor": "bluebob",
            "clip": "KnockOver",
            "prop": "coin-chest"
          },
          {
            "type": "catch",
            "actor": "krabs",
            "clip": "Catch",
            "prop": "coin-chest"
          }
        ],
        "direction": "The chest tips off the counter. Krabs reaches for it, but the lid catches his claw and the chest rolls through the open door.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "wood scrape",
            "metal rattle"
          ]
        },
        "duration": 2.4
      },
      {
        "shot": "intro-3a-01",
        "scene": "town",
        "camera": "tracking",
        "cast": [
          "bluebob",
          "krabs"
        ],
        "actions": [
          {
            "type": "roll",
            "actor": "krabs",
            "clip": "Roll",
            "prop": "coin-chest"
          },
          {
            "type": "startle",
            "actor": "bluebob",
            "clip": "Startle"
          }
        ],
        "direction": "The chest bounces down the steps, spins across the street, and heads for a coral rock while BlueBob freezes with one plate in his hands.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "rolling chest",
            "tiny gasp"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "Oops.",
          "emotion": "sheepish",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "intro-3d",
        "scene": "town",
        "camera": "reveal",
        "cast": [
          "bluebob",
          "krabs",
          "zap"
        ],
        "actions": [
          {
            "type": "burst-open",
            "actor": "krabs",
            "clip": "BurstOpen",
            "prop": "coin-chest"
          },
          {
            "type": "accuse",
            "actor": "krabs",
            "clip": "Accuse"
          },
          {
            "type": "swarm-carry",
            "actor": "zap",
            "clip": "SwarmCarry",
            "prop": "coin-bag"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch"
          }
        ],
        "direction": "The chest hits the rock and bursts open. Coins arc into seaweed and caves as a swarm of angry Zap Jellyfish carries one bag away.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "chest burst",
            "coin cascade",
            "electric swarm"
          ]
        },
        "dialogue": {
          "speaker": "krabs",
          "text": "BLUEBOB! Me coins! Me precious coins!",
          "emotion": "angry",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "intro-4b",
        "scene": "town",
        "camera": "two-shot",
        "cast": [
          "bluebob",
          "krabs"
        ],
        "actions": [
          {
            "type": "point",
            "actor": "krabs",
            "clip": "Point"
          },
          {
            "type": "brace",
            "actor": "bluebob",
            "clip": "Brace"
          }
        ],
        "direction": "Krabs points down the coin trail toward the wild Jelly Fields; BlueBob shoulders a satchel and faces the route.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "stern sting"
          ]
        },
        "dialogue": {
          "speaker": "krabs",
          "text": "You lost it, so you find it! Collect jelly, bring back me coins, and grab any Treasure Patties.",
          "emotion": "angry",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "intro-4d-02",
        "scene": "town",
        "camera": "close",
        "cast": [
          "bluebob",
          "krabs"
        ],
        "actions": [
          {
            "type": "discover",
            "actor": "bluebob",
            "clip": "Discover",
            "prop": "jelly-drop"
          },
          {
            "type": "watch",
            "actor": "krabs",
            "clip": "Watch"
          }
        ],
        "direction": "A glowing drop of jelly pulses beside the road. BlueBob picks it up, turns it in the light, and Krabs reluctantly watches.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "magic chime"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "I found a jelly drop. I can make sandwiches and calm those jellyfish!",
          "emotion": "determined",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "intro-4e-02",
        "scene": "town",
        "camera": "wide",
        "cast": [
          "bluebob",
          "krabs"
        ],
        "actions": [
          {
            "type": "build",
            "actor": "bluebob",
            "clip": "Build",
            "prop": "sandwich"
          },
          {
            "type": "collect",
            "actor": "bluebob",
            "clip": "Collect",
            "prop": "jelly-jars"
          },
          {
            "type": "point",
            "actor": "krabs",
            "clip": "Point"
          }
        ],
        "direction": "BlueBob packs a fresh sandwich beside the jelly jars, gives Krabs a confident salute, and steps onto the coin trail.",
        "transition": "gameplay-handoff",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "bright sting"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "Jelly sandwiches, coin rescue, Treasure Patties—I'm ready!",
          "emotion": "triumphant",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      }
    ]
  },
  "t2": {
    "id": "t2",
    "title": "The Jelly Trail",
    "beats": [
      {
        "shot": "t2-1a-02",
        "scene": "jellytrail",
        "camera": "wide",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "walk",
            "actor": "bluebob",
            "clip": "Walk",
            "prop": "coins"
          },
          {
            "type": "threaten",
            "actor": "zap",
            "clip": "Threaten"
          }
        ],
        "direction": "BlueBob follows the scattered coins into the Jelly Fields. Zap blocks the only path, buzzing over a trail marker.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "electric warning"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "The coin trail leads to Jelly Fields. Please let me pass.",
          "emotion": "determined",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t2-1b-02",
        "scene": "jellytrail",
        "camera": "close",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "throw",
            "actor": "bluebob",
            "clip": "Throw",
            "prop": "sandwich",
            "target": "zap"
          },
          {
            "type": "eat",
            "actor": "zap",
            "clip": "Eat",
            "prop": "sandwich"
          }
        ],
        "direction": "Zap dives. BlueBob tosses the tiny sandwich into the jellyfish bell; Zap catches it mid-flight and bites.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "sandwich toss",
            "soft zap"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "Mmm... I can think again!",
          "emotion": "gentle",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t2-1c-02",
        "scene": "jellytrail",
        "camera": "two-shot",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "soften-glow",
            "actor": "zap",
            "clip": "Calm"
          },
          {
            "type": "talk",
            "actor": "zap",
            "clip": "Talk"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch"
          }
        ],
        "direction": "Zap’s angry glow fades to a soft blue. She points to the purple sparks clinging to the field and names the cause.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "glow release"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "The Jelly King's magic made us guard the coins.",
          "emotion": "worried",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t2-1c-03",
        "scene": "jellytrail",
        "camera": "reveal",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "calm",
            "actor": "bluebob",
            "clip": "Calm",
            "prop": "sticky-sandwich"
          },
          {
            "type": "calm",
            "actor": "zap",
            "clip": "Calm"
          },
          {
            "type": "reveal",
            "actor": "bluebob",
            "clip": "Reveal",
            "prop": "coin-bag"
          }
        ],
        "direction": "BlueBob holds up the next sandwich while Zap floats aside. The path opens and the first coin bag becomes visible on a coral branch.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "warm shimmer"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "It worked! Calm jellyfish, open path.",
          "emotion": "triumphant",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t2-2a-02",
        "scene": "jellytrail",
        "camera": "tracking",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "collect",
            "actor": "bluebob",
            "clip": "Collect",
            "prop": "coin-bag"
          },
          {
            "type": "point",
            "actor": "zap",
            "clip": "Point"
          }
        ],
        "direction": "Zap points through the newly peaceful field to the hanging bag, then leads BlueBob toward the darker coral route.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "bag pickup"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "That bag is Money Krabs'. I saw another trail carried toward Coral Cave.",
          "emotion": "determined",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t2-3-01",
        "scene": "coralcave",
        "camera": "wide",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "walk",
            "actor": "bluebob",
            "clip": "Walk"
          },
          {
            "type": "point",
            "actor": "zap",
            "clip": "Point"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch"
          }
        ],
        "direction": "BlueBob pockets the recovered bag. Coin glints pull both friends to the mouth of Coral Cave where violet webs flicker.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "cave rumble"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "One bag recovered. The coin trail points to Coral Cave.",
          "emotion": "triumphant",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t2-3-04",
        "scene": "coralcave",
        "camera": "reveal",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "build",
            "actor": "bluebob",
            "clip": "Build",
            "prop": "sticky-sandwich"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          },
          {
            "type": "repair",
            "actor": "bluebob",
            "clip": "Repair",
            "prop": "web"
          }
        ],
        "direction": "BlueBob mixes the next recipe while Zap watches the electric web pulse across the tunnel entrance.",
        "transition": "gameplay-handoff",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "jelly whisk"
          ]
        }
      }
    ]
  },
  "t3": {
    "id": "t3",
    "title": "Coral Cave Trouble and the Seaweed Maze",
    "beats": [
      {
        "shot": "t3-1b-01",
        "scene": "seaweed",
        "camera": "wide",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "walk",
            "actor": "bluebob",
            "clip": "Walk"
          },
          {
            "type": "walk",
            "actor": "zap",
            "clip": "Walk"
          },
          {
            "type": "reveal",
            "actor": "bluebob",
            "clip": "Reveal",
            "prop": "coin-bag"
          }
        ],
        "direction": "A recovered bag swings from BlueBob’s satchel as the pair enter the twisting Seaweed Maze. A second bag is tangled ahead.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "seaweed rustle"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "Another bag! The trail keeps moving through this maze.",
          "emotion": "bright",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t3-2a-01",
        "scene": "seaweed",
        "camera": "reaction",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "rotate-sign",
            "actor": "zap",
            "clip": "Point",
            "prop": "sign"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch"
          },
          {
            "type": "hesitate",
            "actor": "zap",
            "clip": "Hesitate"
          }
        ],
        "direction": "Every sign now points a different way. BlueBob spins one sign back and finds the same rock behind him.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "wooden squeak"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "Every sign points somewhere different.",
          "emotion": "dry",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t3-2a-02",
        "scene": "seaweed",
        "camera": "reveal",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "reveal",
            "actor": "zap",
            "clip": "Reveal"
          },
          {
            "type": "talk",
            "actor": "zap",
            "clip": "Talk"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch"
          }
        ],
        "direction": "Zap reveals a cluster of small frightened fish behind the rock. The fish gesture toward the distant purple crown glow.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "muffled school of fish"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "The King moved the signs. The jellyfish became wild when he took over.",
          "emotion": "worried",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t3-2b-02",
        "scene": "seaweed",
        "camera": "two-shot",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "point",
            "actor": "bluebob",
            "clip": "Point"
          },
          {
            "type": "protect",
            "actor": "bluebob",
            "clip": "Protect",
            "prop": "sandwich"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          }
        ],
        "direction": "BlueBob kneels to reassure the fish, then points to the clean route he can make with the sandwich jar.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "hopeful chord"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "Then I'll fix the jellyfish, not just find the coins.",
          "emotion": "determined",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t3-2b-04",
        "scene": "seaweed",
        "camera": "wide",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "break-spell",
            "actor": "bluebob",
            "clip": "BreakSpell",
            "prop": "sandwich"
          },
          {
            "type": "dance",
            "actor": "zap",
            "clip": "Dance"
          },
          {
            "type": "walk",
            "actor": "bluebob",
            "clip": "Walk"
          }
        ],
        "direction": "A sandwich clears the final seaweed gate. The little fish cheer as BlueBob and Zap follow the next coin glint.",
        "transition": "gameplay-handoff",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "seaweed parting"
          ]
        }
      }
    ]
  },
  "t4": {
    "id": "t4",
    "title": "Bubble Bridge",
    "beats": [
      {
        "shot": "t4-1a-03",
        "scene": "bubblebridge",
        "camera": "wide",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "catch",
            "actor": "bluebob",
            "clip": "Catch",
            "prop": "bridge"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          },
          {
            "type": "collect",
            "actor": "bluebob",
            "clip": "Collect",
            "prop": "coins"
          }
        ],
        "direction": "BlueBob steps onto the first bubble of a broken bridge while coins drift in the gap below. Zap hovers at the far bank.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "bubble creak"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "The bridge is breaking under us!",
          "emotion": "afraid",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t4-2a-01",
        "scene": "bubblebridge",
        "camera": "two-shot",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "pop-bubble",
            "actor": "zap",
            "clip": "PopBubble",
            "prop": "bridge"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch"
          },
          {
            "type": "point",
            "actor": "zap",
            "clip": "Point"
          }
        ],
        "direction": "Zap pops the next bubble as BlueBob reaches for it. BlueBob raises the sticky recipe; the bridge’s empty sockets glow.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "bubble pop"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "Sticky jelly calms the guards and seals each bubble.",
          "emotion": "determined",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t4-2a-02",
        "scene": "bubblebridge",
        "camera": "low",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "brace",
            "actor": "bluebob",
            "clip": "Brace"
          },
          {
            "type": "pop-bubble",
            "actor": "zap",
            "clip": "PopBubble",
            "prop": "bridge"
          },
          {
            "type": "protect",
            "actor": "bluebob",
            "clip": "Protect",
            "prop": "sticky-sandwich"
          }
        ],
        "direction": "BlueBob braces on one surviving bubble while Zap tests his patience with one last electric spark.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "comic wobble"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "Step by step. Please stop popping the bridge.",
          "emotion": "dry",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t4-2b-02",
        "scene": "bubblebridge",
        "camera": "tracking",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "build",
            "actor": "bluebob",
            "clip": "Build",
            "prop": "sticky-sandwich"
          },
          {
            "type": "calm",
            "actor": "zap",
            "clip": "Calm"
          },
          {
            "type": "repair",
            "actor": "bluebob",
            "clip": "Repair",
            "prop": "bridge"
          }
        ],
        "direction": "BlueBob smears sticky jelly across the sockets. Zap’s glow softens and the bridge grows behind his feet.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "bridge reform"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "Now cross before I change my mind.",
          "emotion": "dry",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t4-2b-04",
        "scene": "bubblebridge",
        "camera": "reveal",
        "cast": [
          "bluebob",
          "zap",
          "king"
        ],
        "actions": [
          {
            "type": "reveal",
            "actor": "king",
            "clip": "Reveal"
          },
          {
            "type": "steal",
            "actor": "king",
            "clip": "Steal",
            "prop": "coin-bag"
          },
          {
            "type": "summon-lightning",
            "actor": "king",
            "clip": "SummonLightning"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch"
          },
          {
            "type": "chase",
            "actor": "zap",
            "clip": "Chase"
          }
        ],
        "direction": "At the far bank, Treasure Patties float in a basket. The Jelly King flashes into the shot, grabs the marked coin bag, and rockets upward in a crackle of purple light.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "royal buzz",
            "lightning snap"
          ]
        },
        "dialogue": {
          "speaker": "king",
          "text": "BZZZT! Shiny bag! My jellyfish will love it!",
          "emotion": "triumphant",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t4-3",
        "scene": "bubblebridge",
        "camera": "chase",
        "cast": [
          "bluebob",
          "zap",
          "king"
        ],
        "actions": [
          {
            "type": "challenge",
            "actor": "bluebob",
            "clip": "Challenge"
          },
          {
            "type": "chase",
            "actor": "zap",
            "clip": "Chase"
          },
          {
            "type": "steal",
            "actor": "king",
            "clip": "Steal",
            "prop": "coin-bag"
          }
        ],
        "direction": "BlueBob points at the fleeing King. Zap pulls him back from the edge while the King disappears toward the sunken ship.",
        "transition": "gameplay-handoff",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "whoosh"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "Hey! That coin bag belongs to Money Krabs!",
          "emotion": "angry",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      }
    ]
  },
  "t5": {
    "id": "t5",
    "title": "The Sunken Snack Ship",
    "beats": [
      {
        "shot": "t5-1a-01",
        "scene": "ship",
        "camera": "wide",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "walk",
            "actor": "bluebob",
            "clip": "Walk",
            "prop": "ship"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          }
        ],
        "direction": "The coin trail dives through a rusted hatch into a sunken snack ship. BlueBob plants his feet on the deck while Zap scans the barrels.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "hull groan"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "The trail ends at a sunken snack ship.",
          "emotion": "determined",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t5-1b",
        "scene": "ship",
        "camera": "tracking",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "discover",
            "actor": "bluebob",
            "clip": "Discover",
            "prop": "coin-bag"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          },
          {
            "type": "reveal",
            "actor": "bluebob",
            "clip": "Reveal",
            "prop": "coins"
          }
        ],
        "direction": "A torn bag trail leads between chests and barrels. Purple jelly residue stains every shiny coin and patty.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "distant creak"
          ]
        }
      },
      {
        "shot": "t5-1c",
        "scene": "ship",
        "camera": "close",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "point",
            "actor": "bluebob",
            "clip": "Point"
          },
          {
            "type": "talk",
            "actor": "zap",
            "clip": "Talk"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch"
          }
        ],
        "direction": "BlueBob lifts a coin beside a sticky purple smear. Zap points to a barrel where a jellyfish nibbles a Treasure Patty like candy.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "greedy nibble"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "They think shiny things are food because of the King’s magic jelly.",
          "emotion": "worried",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t5-2-02",
        "scene": "ship",
        "camera": "reaction",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "offer",
            "actor": "zap",
            "clip": "Offer",
            "prop": "sour-sandwich",
            "target": "bluebob"
          },
          {
            "type": "open",
            "actor": "bluebob",
            "clip": "Open",
            "prop": "sour-sandwich"
          },
          {
            "type": "tilt",
            "actor": "bluebob",
            "clip": "Tilt",
            "prop": "ship"
          }
        ],
        "direction": "Zap hands BlueBob a sour jelly jar. He twists the lid toward the occupied barrel as the ship begins a slow, dangerous tilt.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "jar pop",
            "ship tilt"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "Sour jelly should break the spell.",
          "emotion": "determined",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t5-2-03",
        "scene": "ship",
        "camera": "low",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "offer",
            "actor": "bluebob",
            "clip": "Offer",
            "prop": "sour-sandwich",
            "target": "zap"
          },
          {
            "type": "eat",
            "actor": "zap",
            "clip": "Eat",
            "prop": "sour-sandwich"
          },
          {
            "type": "break-spell",
            "actor": "zap",
            "clip": "BreakSpell"
          },
          {
            "type": "dive",
            "actor": "bluebob",
            "clip": "Dive"
          },
          {
            "type": "fall",
            "actor": "bluebob",
            "clip": "Fall",
            "prop": "coins"
          }
        ],
        "direction": "BlueBob offers a sour sandwich through the barrel slats. The jellyfish spit out the patty, the captain’s coin pile slides into a deck hole, and BlueBob dives after it.",
        "transition": "gameplay-handoff",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "sour squish",
            "coin avalanche",
            "water rush"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "Room service—one sour sandwich for the barrel!",
          "emotion": "bright",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      }
    ]
  },
  "t6": {
    "id": "t6",
    "title": "Deep Sea Drop",
    "beats": [
      {
        "shot": "t6-1c-01",
        "scene": "ship",
        "camera": "wide",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "collect",
            "actor": "bluebob",
            "clip": "Collect",
            "prop": "coin-bag"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          }
        ],
        "direction": "BlueBob hangs above the deep opening, sees the next coin bag below, and looks back at the trail he started.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "deep heartbeat"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "I made the mess. I'm going to fix it.",
          "emotion": "determined",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t6-tilt-01",
        "scene": "ship",
        "camera": "reaction",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "tilt",
            "actor": "bluebob",
            "clip": "Tilt",
            "prop": "ship"
          },
          {
            "type": "brace",
            "actor": "zap",
            "clip": "Brace"
          },
          {
            "type": "catch",
            "actor": "bluebob",
            "clip": "Catch",
            "prop": "coin-bag"
          }
        ],
        "direction": "The ship rolls hard. The bag slides toward the hole and a folded cargo chart catches beneath its tie.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "wood strain"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "There’s the next bag—above the deep drop.",
          "emotion": "afraid",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t6-tilt-02",
        "scene": "ship",
        "camera": "low",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "fall",
            "actor": "bluebob",
            "clip": "Fall"
          },
          {
            "type": "catch",
            "actor": "zap",
            "clip": "Catch"
          },
          {
            "type": "brace",
            "actor": "zap",
            "clip": "Brace"
          }
        ],
        "direction": "The deck gives way. BlueBob drops with the bag; Zap catches his hand for one breath before both plunge into blue darkness.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "deck crack",
            "falling bubbles"
          ]
        },
        "duration": 3.1
      },
      {
        "shot": "t6-2a-01",
        "scene": "deepsea",
        "camera": "wide",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "fall",
            "actor": "bluebob",
            "clip": "Fall"
          },
          {
            "type": "fall",
            "actor": "zap",
            "clip": "Fall"
          },
          {
            "type": "reveal",
            "actor": "zap",
            "clip": "Reveal",
            "prop": "clam"
          }
        ],
        "direction": "BlueBob and Zap land beside a giant clam. The recovered bag is wedged in its shell while bright blue jellies circle above.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "deep splash"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "Found the bag. The clam is hiding something.",
          "emotion": "worried",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t6-2b-01",
        "scene": "deepsea",
        "camera": "two-shot",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "protect",
            "actor": "bluebob",
            "clip": "Protect"
          },
          {
            "type": "open",
            "actor": "zap",
            "clip": "Open",
            "prop": "clam"
          },
          {
            "type": "project-map",
            "actor": "zap",
            "clip": "ProjectMap",
            "prop": "map"
          }
        ],
        "direction": "BlueBob shields the clam from a passing spark. Zap gestures to the shell seam; the clam opens and reveals a map under the bag.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "clam hinge",
            "map shimmer"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "The clam opened. A map to the Jelly King!",
          "emotion": "triumphant",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t6-2b-02",
        "scene": "deepsea",
        "camera": "reveal",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "build",
            "actor": "bluebob",
            "clip": "Build",
            "prop": "deepsea-sandwich"
          },
          {
            "type": "project-map",
            "actor": "zap",
            "clip": "ProjectMap",
            "prop": "map"
          },
          {
            "type": "point",
            "actor": "zap",
            "clip": "Point"
          }
        ],
        "direction": "BlueBob mixes the deep-sea jelly into a powerful sandwich. The map projects a route through Patty Reef and into the storm.",
        "transition": "gameplay-handoff",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "map projection"
          ]
        }
      }
    ]
  },
  "t7": {
    "id": "t7",
    "title": "The Patty Reef",
    "beats": [
      {
        "shot": "t7-2a-02",
        "scene": "deepsea",
        "camera": "close",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "collect",
            "actor": "bluebob",
            "clip": "Collect",
            "prop": "patty"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          },
          {
            "type": "catch",
            "actor": "bluebob",
            "clip": "Catch",
            "prop": "coin-bag"
          }
        ],
        "direction": "The clam snaps shut around the empty space where the bag was. BlueBob catches a golden Treasure Patty before it drifts away.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "patty sparkle"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "Treasure Patties! The clam’s reward has teeth.",
          "emotion": "bright",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t7-2b-01",
        "scene": "deepsea",
        "camera": "tracking",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "point",
            "actor": "zap",
            "clip": "Point"
          },
          {
            "type": "project-map",
            "actor": "zap",
            "clip": "ProjectMap",
            "prop": "map"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch"
          }
        ],
        "direction": "Zap holds the projected map over the recovered bag. A red route ends at a colorful reef shaped like a crown.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "map ping"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "The map points to Patty Reef.",
          "emotion": "determined",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t7-3-01",
        "scene": "pattyreef",
        "camera": "wide",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "walk",
            "actor": "bluebob",
            "clip": "Walk"
          },
          {
            "type": "walk",
            "actor": "zap",
            "clip": "Walk"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch",
            "prop": "patty"
          }
        ],
        "direction": "Patty Reef looks peaceful: floating golden patties form a bright path, with friendly bubbles hiding the electric weather beyond.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "playful marimba"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "The reef is pretty... which means it’s probably a trap.",
          "emotion": "dry",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t7-3-02",
        "scene": "pattyreef",
        "camera": "reaction",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "trap",
            "actor": "zap",
            "clip": "Trap",
            "prop": "patty"
          },
          {
            "type": "surround",
            "actor": "zap",
            "clip": "Surround"
          },
          {
            "type": "brace",
            "actor": "bluebob",
            "clip": "Brace"
          },
          {
            "type": "build",
            "actor": "bluebob",
            "clip": "Build",
            "prop": "giant-sandwich"
          }
        ],
        "direction": "Zap Jellyfish snap into a ring around the reef and use the patties as bait. BlueBob braces behind a giant sandwich plan.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "electric storm"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "A giant sandwich can calm the whole reef.",
          "emotion": "determined",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t7-3-04",
        "scene": "pattyreef",
        "camera": "reveal",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "throw",
            "actor": "bluebob",
            "clip": "Throw",
            "prop": "giant-sandwich",
            "target": "zap"
          },
          {
            "type": "calm",
            "actor": "zap",
            "clip": "Calm"
          },
          {
            "type": "dance",
            "actor": "zap",
            "clip": "Dance"
          },
          {
            "type": "point",
            "actor": "zap",
            "clip": "Point"
          }
        ],
        "direction": "The giant sandwich floats through the ring. The jellies stop attacking and dance; one friendly jellyfish points toward a storm tower.",
        "transition": "gameplay-handoff",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "reef choir"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "They’re peaceful. That friendly jellyfish knows the hideout.",
          "emotion": "gentle",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      }
    ]
  },
  "t8": {
    "id": "t8",
    "title": "Stormy Jelly Skies",
    "beats": [
      {
        "shot": "t8-1a-02",
        "scene": "pattyreef",
        "camera": "wide",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "collect",
            "actor": "bluebob",
            "clip": "Collect",
            "prop": "jelly-jars"
          },
          {
            "type": "protect",
            "actor": "zap",
            "clip": "Protect"
          },
          {
            "type": "build",
            "actor": "bluebob",
            "clip": "Build",
            "prop": "sandwich"
          }
        ],
        "direction": "The reef settles behind them. BlueBob and Zap load storm jelly into the satchel as the first lightning bubbles roll overhead.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "storm distant"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "The whole reef is calm. Now we climb.",
          "emotion": "triumphant",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t8-1b",
        "scene": "pattyreef",
        "camera": "reveal",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "collect",
            "actor": "bluebob",
            "clip": "Collect",
            "prop": "patty"
          },
          {
            "type": "celebrate",
            "actor": "bluebob",
            "clip": "Celebrate"
          },
          {
            "type": "point",
            "actor": "zap",
            "clip": "Point"
          }
        ],
        "direction": "BlueBob grabs the last floating patty for the bonus treasure. Zap points to the tall coral tower and the storm beyond.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "patty pickup"
          ]
        },
        "duration": 2.3
      },
      {
        "shot": "t8-1c",
        "scene": "storm",
        "camera": "wide",
        "cast": [
          "bluebob",
          "zap"
        ],
        "actions": [
          {
            "type": "walk",
            "actor": "bluebob",
            "clip": "Walk"
          },
          {
            "type": "summon-lightning",
            "actor": "zap",
            "clip": "SummonLightning"
          },
          {
            "type": "point",
            "actor": "zap",
            "clip": "Point"
          }
        ],
        "direction": "Lightning bubbles flash across the tower. Zap leads BlueBob up a narrow coral stair while the Royal Jelly Cave glows on the far side.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "wind and lightning"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "The Royal Jelly Cave is above the storm.",
          "emotion": "worried",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t8-2-02",
        "scene": "storm",
        "camera": "close",
        "cast": [
          "bluebob",
          "zap",
          "king"
        ],
        "actions": [
          {
            "type": "startle",
            "actor": "bluebob",
            "clip": "Startle"
          },
          {
            "type": "collect",
            "actor": "zap",
            "clip": "Collect",
            "prop": "jelly-jars"
          },
          {
            "type": "summon-wave",
            "actor": "king",
            "clip": "SummonWave"
          },
          {
            "type": "summon-lightning",
            "actor": "king",
            "clip": "SummonLightning"
          },
          {
            "type": "threaten",
            "actor": "king",
            "clip": "Threaten"
          }
        ],
        "direction": "At the tower top, BlueBob reaches for the biggest coin bag. The Jelly King erupts from the lightning cloud and sends a wave of Zap Jellyfish across the ledge.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "royal thunder"
          ]
        },
        "dialogue": {
          "speaker": "king",
          "text": "BZZZT! No one takes my shiny snacks!",
          "emotion": "angry",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t8-2-04",
        "scene": "storm",
        "camera": "tracking",
        "cast": [
          "bluebob",
          "zap",
          "king"
        ],
        "actions": [
          {
            "type": "protect",
            "actor": "bluebob",
            "clip": "Protect",
            "prop": "sandwich"
          },
          {
            "type": "challenge",
            "actor": "bluebob",
            "clip": "Challenge"
          },
          {
            "type": "steal",
            "actor": "king",
            "clip": "Steal",
            "prop": "coin-bag"
          },
          {
            "type": "chase",
            "actor": "zap",
            "clip": "Chase"
          }
        ],
        "direction": "BlueBob plants the storm sandwich between himself and the sparks. The King snatches the final bag and escapes through a crack in the coral wall.",
        "transition": "gameplay-handoff",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "bag yank",
            "cave collapse"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "They’re not snacks. They belong to Money Krabs!",
          "emotion": "angry",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      }
    ]
  },
  "t9": {
    "id": "t9",
    "title": "Royal Jelly Cave",
    "beats": [
      {
        "shot": "t9-1b-01",
        "scene": "royalcave",
        "camera": "wide",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "enter",
            "actor": "bluebob",
            "clip": "Walk"
          },
          {
            "type": "point",
            "actor": "king",
            "clip": "Point",
            "prop": "throne"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          },
          {
            "type": "reveal",
            "actor": "bluebob",
            "clip": "Reveal",
            "prop": "coin-chest"
          }
        ],
        "direction": "The map leads into a glowing cave. Hundreds of jellyfish hang under a purple command pulse while the King stacks coins into a lonely throne.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "cave choir"
          ]
        },
        "dialogue": {
          "speaker": "king",
          "text": "If I build a bigger throne, everyone will notice me.",
          "emotion": "worried",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t9-1b-02",
        "scene": "royalcave",
        "camera": "two-shot",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "point",
            "actor": "bluebob",
            "clip": "Point"
          },
          {
            "type": "protect",
            "actor": "bluebob",
            "clip": "Protect"
          },
          {
            "type": "watch",
            "actor": "king",
            "clip": "Watch"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          }
        ],
        "direction": "BlueBob steps between the King and the trapped jellyfish, keeping the recovered map visible as proof that the town is waiting.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "quiet resolve"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "You don’t have to steal to have friends.",
          "emotion": "gentle",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t9-1b-04",
        "scene": "royalcave",
        "camera": "reaction",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "hesitate",
            "actor": "king",
            "clip": "Hesitate"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          }
        ],
        "direction": "The King’s crown dips. He looks from the empty cave entrance to his silent throne and admits why he keeps collecting.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "soft room tone"
          ]
        },
        "dialogue": {
          "speaker": "king",
          "text": "They never come to my cave.",
          "emotion": "sheepish",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t9-2a-01",
        "scene": "royalcave",
        "camera": "close",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "command-pulse",
            "actor": "king",
            "clip": "CommandPulse"
          },
          {
            "type": "sever-command",
            "actor": "zap",
            "clip": "SeverCommand"
          },
          {
            "type": "protect",
            "actor": "bluebob",
            "clip": "Protect"
          },
          {
            "type": "surround",
            "actor": "zap",
            "clip": "Surround"
          }
        ],
        "direction": "The command pulse flares. Zap cuts one strand of purple light while BlueBob shields the freed jellyfish from the King’s sparks.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "spell tension"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "They’re scared of you. Your magic jelly is hurting them.",
          "emotion": "worried",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t9-2a-05",
        "scene": "royalcave",
        "camera": "reveal",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "build",
            "actor": "bluebob",
            "clip": "Build",
            "prop": "ultimate-sandwich"
          },
          {
            "type": "watch",
            "actor": "king",
            "clip": "Watch"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          },
          {
            "type": "open",
            "actor": "bluebob",
            "clip": "Open"
          }
        ],
        "direction": "BlueBob gathers the rarest jelly crystals and builds an ultimate sandwich that glows in every reef color. The cave opens toward the heart chamber.",
        "transition": "gameplay-handoff",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "rainbow jelly"
          ]
        }
      }
    ]
  },
  "t10": {
    "id": "t10",
    "title": "Jelly King Showdown",
    "beats": [
      {
        "shot": "t10-1a-03",
        "scene": "throne",
        "camera": "wide",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "walk",
            "actor": "bluebob",
            "clip": "Walk"
          },
          {
            "type": "watch",
            "actor": "king",
            "clip": "Watch",
            "prop": "throne"
          },
          {
            "type": "surround",
            "actor": "zap",
            "clip": "Surround"
          },
          {
            "type": "reveal",
            "actor": "bluebob",
            "clip": "Reveal",
            "prop": "coin-bag"
          }
        ],
        "direction": "The King sits on a throne of coins, patties, and jelly crystals. The final bag is perched above him as the freed jellyfish gather behind BlueBob.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "boss sting"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "A throne can’t make friends. Give back the bag.",
          "emotion": "determined",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t10-1b-02",
        "scene": "throne",
        "camera": "two-shot",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "summon-lightning",
            "actor": "king",
            "clip": "SummonLightning"
          },
          {
            "type": "protect",
            "actor": "bluebob",
            "clip": "Protect"
          },
          {
            "type": "surround",
            "actor": "zap",
            "clip": "Surround"
          },
          {
            "type": "threaten",
            "actor": "king",
            "clip": "Threaten"
          }
        ],
        "direction": "The King launches lightning from the crown. BlueBob dodges behind a crystal while Zap circles the throne to cut off the escape.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "lightning volley"
          ]
        },
        "dialogue": {
          "speaker": "king",
          "text": "I'm the King! I can make them stay!",
          "emotion": "angry",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t10-1b-03",
        "scene": "throne",
        "camera": "close",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "point",
            "actor": "bluebob",
            "clip": "Point"
          },
          {
            "type": "offer",
            "actor": "bluebob",
            "clip": "Offer"
          },
          {
            "type": "hesitate",
            "actor": "king",
            "clip": "Hesitate"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          }
        ],
        "direction": "BlueBob steps into the royal spotlight and lowers his hands. The attack pauses long enough for the ultimate sandwich to glow in his satchel.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "attack hush"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "People choose friends. They don't follow a spell.",
          "emotion": "gentle",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t10-1b-04",
        "scene": "throne",
        "camera": "reaction",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "summon-wave",
            "actor": "king",
            "clip": "SummonWave"
          },
          {
            "type": "threaten",
            "actor": "king",
            "clip": "Threaten"
          },
          {
            "type": "protect",
            "actor": "bluebob",
            "clip": "Protect"
          },
          {
            "type": "protect",
            "actor": "zap",
            "clip": "Protect"
          },
          {
            "type": "collapse-throne",
            "actor": "king",
            "clip": "CollapseThrone",
            "prop": "throne"
          }
        ],
        "direction": "The King answers with a rolling jelly wave. BlueBob and Zap brace together while coins rain from the unstable throne.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "jelly wave"
          ]
        },
        "dialogue": {
          "speaker": "king",
          "text": "Then show me a better way.",
          "emotion": "afraid",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t10-2a-02",
        "scene": "throne",
        "camera": "low",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "sever-command",
            "actor": "zap",
            "clip": "SeverCommand"
          },
          {
            "type": "offer",
            "actor": "bluebob",
            "clip": "Offer",
            "prop": "ultimate-sandwich",
            "target": "king"
          },
          {
            "type": "build",
            "actor": "bluebob",
            "clip": "Build",
            "prop": "ultimate-sandwich"
          },
          {
            "type": "watch",
            "actor": "king",
            "clip": "Watch"
          }
        ],
        "direction": "Zap severs the last command strand. BlueBob holds the ultimate sandwich where the King can choose it, with no force behind the offer.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "spell release"
          ]
        },
        "dialogue": {
          "speaker": "zap",
          "text": "Start with one sandwich and one honest apology.",
          "emotion": "gentle",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "t10-3-04",
        "scene": "throne",
        "camera": "reveal",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "throw",
            "actor": "bluebob",
            "clip": "Throw",
            "prop": "ultimate-sandwich",
            "target": "king"
          },
          {
            "type": "eat",
            "actor": "king",
            "clip": "Eat",
            "prop": "ultimate-sandwich"
          },
          {
            "type": "swallow",
            "actor": "king",
            "clip": "Eat"
          },
          {
            "type": "soften-glow",
            "actor": "king",
            "clip": "Calm"
          },
          {
            "type": "sever-command",
            "actor": "zap",
            "clip": "SeverCommand"
          },
          {
            "type": "collapse-throne",
            "actor": "king",
            "clip": "CollapseThrone",
            "prop": "throne"
          }
        ],
        "direction": "BlueBob throws the ultimate sandwich. The King eats it; every angry spark fades, the throne settles, and the jellyfish return to their calm colors.",
        "transition": "gameplay-handoff",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "sandwich catch",
            "warm chord"
          ]
        },
        "dialogue": {
          "speaker": "king",
          "text": "Bzz… sorry.",
          "emotion": "sheepish",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      }
    ]
  },
  "ending": {
    "id": "ending",
    "title": "The Grand Reopening",
    "beats": [
      {
        "shot": "ending-1b",
        "scene": "throne",
        "camera": "tracking",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "collect",
            "actor": "king",
            "clip": "Collect",
            "prop": "coin-bag"
          },
          {
            "type": "walk",
            "actor": "king",
            "clip": "Walk"
          },
          {
            "type": "walk",
            "actor": "bluebob",
            "clip": "Walk"
          },
          {
            "type": "calm",
            "actor": "zap",
            "clip": "Calm"
          }
        ],
        "direction": "The King carries the final coin bag beside BlueBob. Zap guides the peaceful jellyfish through the open cave toward Bubble Bay.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "bag lift"
          ]
        },
        "dialogue": {
          "speaker": "king",
          "text": "Bzz… sorry. I brought back the last bag.",
          "emotion": "sheepish",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "ending-2a",
        "scene": "throne",
        "camera": "two-shot",
        "cast": [
          "bluebob",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "calm",
            "actor": "bluebob",
            "clip": "Calm"
          },
          {
            "type": "lower",
            "actor": "king",
            "clip": "Lower"
          },
          {
            "type": "walk",
            "actor": "bluebob",
            "clip": "Walk"
          },
          {
            "type": "watch",
            "actor": "zap",
            "clip": "Watch"
          }
        ],
        "direction": "BlueBob accepts the apology with a small nod. The King lowers his crown and keeps both claws visible as they leave the throne room.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "gentle resolve"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "It’s okay. We give the money back together.",
          "emotion": "gentle",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "ending-2b-01",
        "scene": "throne",
        "camera": "wide",
        "cast": [
          "bluebob",
          "krabs",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "enter",
            "actor": "krabs",
            "clip": "Walk"
          },
          {
            "type": "offer-coin",
            "actor": "king",
            "clip": "OfferCoin",
            "prop": "coins",
            "target": "krabs"
          },
          {
            "type": "collect",
            "actor": "krabs",
            "clip": "Collect",
            "prop": "coin-bag"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch"
          }
        ],
        "direction": "Money Krabs arrives at the cave mouth with a counting tray. The King offers one forgotten coin before Krabs reaches for the returned bags.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "coin count"
          ]
        },
        "dialogue": {
          "speaker": "krabs",
          "text": "One… two… three… all of it! Me money is back!",
          "emotion": "triumphant",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "ending-2b-02",
        "scene": "throne",
        "camera": "reaction",
        "cast": [
          "bluebob",
          "krabs",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "collect",
            "actor": "bluebob",
            "clip": "Collect",
            "prop": "coin-bag"
          },
          {
            "type": "collect",
            "actor": "king",
            "clip": "Collect",
            "prop": "coin-bag"
          },
          {
            "type": "point",
            "actor": "krabs",
            "clip": "Point"
          }
        ],
        "direction": "Krabs checks the last bag, then looks past the coins to the calm jellyfish and the rescued town route behind them.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "relieved laugh"
          ]
        },
        "dialogue": {
          "speaker": "krabs",
          "text": "You saved Bubble Bay too, BlueBob.",
          "emotion": "bright",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "ending-3a",
        "scene": "reopening",
        "camera": "wide",
        "cast": [
          "bluebob",
          "krabs",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "walk",
            "actor": "bluebob",
            "clip": "Walk"
          },
          {
            "type": "walk",
            "actor": "king",
            "clip": "Walk"
          },
          {
            "type": "walk",
            "actor": "zap",
            "clip": "Walk"
          },
          {
            "type": "watch",
            "actor": "krabs",
            "clip": "Watch"
          },
          {
            "type": "decorate",
            "actor": "king",
            "clip": "Decorate",
            "prop": "decorations"
          }
        ],
        "direction": "The group walks back into Bubble Bay. Fish hang lights while the King carries a coil of decorations instead of a stolen treasure.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "town cheers"
          ]
        },
        "duration": 2.8
      },
      {
        "shot": "ending-4a-01",
        "scene": "reopening",
        "camera": "close",
        "cast": [
          "bluebob",
          "krabs",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "offer-coin",
            "actor": "king",
            "clip": "OfferCoin",
            "prop": "coins",
            "target": "krabs"
          },
          {
            "type": "collect",
            "actor": "krabs",
            "clip": "Collect",
            "prop": "coins"
          },
          {
            "type": "decorate",
            "actor": "king",
            "clip": "Decorate",
            "prop": "decorations"
          },
          {
            "type": "watch",
            "actor": "bluebob",
            "clip": "Watch"
          }
        ],
        "direction": "The King offers the forgotten coin with both tentacles. Krabs snatches it, then points at the Shack sign and hands the King a decoration.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "coin snatch",
            "comic beat"
          ]
        },
        "dialogue": {
          "speaker": "krabs",
          "text": "Apology accepted. Now help decorate.",
          "emotion": "dry",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "ending-4c-02",
        "scene": "reopening",
        "camera": "two-shot",
        "cast": [
          "bluebob",
          "krabs",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "point",
            "actor": "bluebob",
            "clip": "Point"
          },
          {
            "type": "reward",
            "actor": "krabs",
            "clip": "Reward",
            "prop": "patty",
            "target": "bluebob"
          },
          {
            "type": "decorate",
            "actor": "king",
            "clip": "Decorate",
            "prop": "decorations"
          },
          {
            "type": "decorate",
            "actor": "zap",
            "clip": "Decorate",
            "prop": "decorations"
          }
        ],
        "direction": "Krabs opens a serving tray and slides one golden Treasure Patty toward BlueBob while the King and Zap string lights overhead.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "tray slide"
          ]
        },
        "dialogue": {
          "speaker": "bluebob",
          "text": "Does that mean I get a reward?",
          "emotion": "bright",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "ending-4c-03",
        "scene": "reopening",
        "camera": "reaction",
        "cast": [
          "bluebob",
          "krabs",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "offer",
            "actor": "krabs",
            "clip": "Offer",
            "prop": "patty",
            "target": "bluebob"
          },
          {
            "type": "catch",
            "actor": "bluebob",
            "clip": "Catch",
            "prop": "patty"
          },
          {
            "type": "decorate",
            "actor": "king",
            "clip": "Decorate",
            "prop": "decorations"
          },
          {
            "type": "celebrate",
            "actor": "zap",
            "clip": "Celebrate"
          }
        ],
        "direction": "BlueBob lifts the patty like a trophy. Krabs leans in with a warning while the King places the final light on the Shack roof.",
        "transition": "cut",
        "audio": {
          "music": "reef pulse",
          "sfx": [
            "crowd chuckle"
          ]
        },
        "dialogue": {
          "speaker": "krabs",
          "text": "Here. One Treasure Patty. Don't get used to it.",
          "emotion": "dry",
          "performance": "Play the physical beat first, then speak toward the visible listener."
        }
      },
      {
        "shot": "ending-5b",
        "scene": "reopening",
        "camera": "wide",
        "cast": [
          "bluebob",
          "krabs",
          "king",
          "zap"
        ],
        "actions": [
          {
            "type": "celebrate",
            "actor": "bluebob",
            "clip": "Celebrate"
          },
          {
            "type": "serve",
            "actor": "krabs",
            "clip": "Serve",
            "prop": "sandwich",
            "target": "bluebob"
          },
          {
            "type": "dance",
            "actor": "king",
            "clip": "Dance"
          },
          {
            "type": "group-laugh",
            "actor": "zap",
            "clip": "GroupLaugh"
          },
          {
            "type": "decorate",
            "actor": "king",
            "clip": "Decorate",
            "prop": "decorations"
          }
        ],
        "direction": "The Krabby Shack reopens under glowing lights. BlueBob, Krabs, the King, Zap, and the whole town share jelly sandwiches and Treasure Patties as the camera pulls back over Bubble Bay.",
        "transition": "cut",
        "audio": {
          "music": "Grand reopening",
          "sfx": [
            "party music",
            "happy jellyfish"
          ]
        },
        "duration": 4.2,
        "titleCards": [
          "THE END",
          "THANKS FOR PLAYING BLUEBOB AND THE MISSING TREASURE PATTIES!"
        ]
      }
    ]
  }
}

export function getIntro(): Cutscene { return CUTSCENES.intro }
export function getEnding(): Cutscene { return CUTSCENES.ending }
export function getTransition(level: number): Cutscene | null { return CUTSCENES[`t${level}`] ?? null }
